import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db } from '../../firebase';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/ui/Layout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import StatusPill from '../../components/ui/StatusPill';
import Modal from '../../components/ui/Modal';
import { toast } from '../../components/ui/Toast';
import { useTranslation } from '../../i18n/LanguageContext';
import {
  IconArrowLeft,
  IconCheck,
  IconUser,
  IconClock,
  IconMapPin,
  IconAlertTriangle,
  IconTractor,
} from '@tabler/icons-react';

export default function NearMissDetail() {
  const { language } = useTranslation();
  const { nearMissId } = useParams();
  const navigate = useNavigate();
  const { orgId, userProfile, role } = useAuth();

  const [nearMiss, setNearMiss] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [correctiveActions, setCorrectiveActions] = useState('');
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!orgId || !nearMissId) return;
      try {
        const snap = await getDoc(doc(db, 'organisations', orgId, 'nearMisses', nearMissId));
        if (snap.exists()) setNearMiss({ id: snap.id, ...snap.data() });
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    };
    load();
  }, [orgId, nearMissId]);

  const handleReview = async () => {
    try {
      await updateDoc(doc(db, 'organisations', orgId, 'nearMisses', nearMissId), {
        status: 'under-review',
        reviewedBy: userProfile?.name,
        reviewedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setNearMiss(prev => ({ ...prev, status: 'under-review', reviewedBy: userProfile?.name }));
      toast.success('Marked as under review');
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const handleClose = async () => {
    if (!correctiveActions.trim()) return;
    setClosing(true);
    try {
      await updateDoc(doc(db, 'organisations', orgId, 'nearMisses', nearMissId), {
        status: 'closed',
        correctiveActions: correctiveActions.trim(),
        closedBy: userProfile?.name,
        closedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setNearMiss(prev => ({ ...prev, status: 'closed', correctiveActions: correctiveActions.trim(), closedBy: userProfile?.name }));
      setShowCloseModal(false);
      toast.success('Near miss closed');
    } catch (err) {
      toast.error('Failed to close near miss');
    }
    setClosing(false);
  };

  const formatDateTime = (ts) => {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString(language, { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)', fontSize: 13, color: 'var(--color-text-tertiary)' }}>Loading...</div>
    </Layout>
  );

  if (!nearMiss) return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)' }}>
        <div onClick={() => navigate('/near-misses')} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--color-text-secondary)', cursor: 'pointer', marginBottom: 16 }}>
          <IconArrowLeft size={15} stroke={1.5} /> Near misses
        </div>
        <div style={{ fontSize: 16, color: 'var(--color-text-secondary)' }}>Near miss not found</div>
      </div>
    </Layout>
  );

  return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)', maxWidth: 680, margin: '0 auto' }}>

        <div onClick={() => navigate('/near-misses')} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--color-text-secondary)', cursor: 'pointer', marginBottom: 20 }}>
          <IconArrowLeft size={15} stroke={1.5} /> Near misses
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <h1 style={{ fontSize: 20, fontWeight: 400 }}>Near miss report</h1>
              <StatusPill status={nearMiss.status || 'open'} size="lg" />
            </div>
            <div style={{ fontSize: 13, color: 'var(--color-text-tertiary)' }}>
              Reported by {nearMiss.reportedBy} · {formatDateTime(nearMiss.reportedAt)}
            </div>
          </div>
          {(role === 'org_admin' || role === 'manager') && nearMiss.status !== 'closed' && (
            <div style={{ display: 'flex', gap: 8 }}>
              {nearMiss.status === 'open' && (
                <Button variant="secondary" size="sm" onClick={handleReview}>
                  Mark under review
                </Button>
              )}
              <Button
                variant="success"
                size="sm"
                icon={<IconCheck size={13} stroke={1.5} />}
                onClick={() => setShowCloseModal(true)}
              >
                Close report
              </Button>
            </div>
          )}
        </div>

        <Card style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 14 }}>Incident details</div>
          <div style={{ fontSize: 13, color: 'var(--color-text-primary)', lineHeight: 1.6, marginBottom: 14, padding: '12px 14px', background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-md)' }}>
            {nearMiss.description || 'No description provided'}
          </div>
          {[
            { icon: IconUser,          label: 'Reported by',           value: nearMiss.reportedBy || '—'                      },
            { icon: IconClock,         label: 'Reported at',           value: formatDateTime(nearMiss.reportedAt)              },
            { icon: IconTractor,       label: 'Machine involved',      value: nearMiss.machineName || '—'                     },
            { icon: IconMapPin,        label: 'Location',              value: nearMiss.location || '—'                        },
            { icon: IconAlertTriangle, label: 'Potential consequence', value: nearMiss.potentialConsequence || '—'            },
            { icon: IconCheck,         label: 'Immediate action',      value: nearMiss.immediateAction || '—'                 },
          ].map(row => {
            const Icon = row.icon;
            return (
              <div key={row.label} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 0', borderBottom: '0.5px solid var(--color-border-light)' }}>
                <Icon size={14} color="var(--color-text-tertiary)" stroke={1.5} style={{ marginTop: 1, flexShrink: 0 }} />
                <span style={{ fontSize: 13, color: 'var(--color-text-secondary)', width: 150, flexShrink: 0 }}>{row.label}</span>
                <span style={{ fontSize: 13, color: 'var(--color-text-primary)', lineHeight: 1.5 }}>{row.value}</span>
              </div>
            );
          })}
        </Card>

        {nearMiss.photos?.length > 0 && (
          <Card style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 14 }}>Photos</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {nearMiss.photos.map((url, i) => (
                <img key={i} src={url} alt="" style={{ width: 120, height: 90, objectFit: 'cover', borderRadius: 'var(--radius-md)', border: '0.5px solid var(--color-border-light)', cursor: 'pointer' }} onClick={() => window.open(url, '_blank')} />
              ))}
            </div>
          </Card>
        )}

        {nearMiss.reviewedBy && (
          <Card style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 10 }}>Review</div>
            <div style={{ fontSize: 13, color: 'var(--color-text-primary)' }}>Under review by {nearMiss.reviewedBy}</div>
            <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 4 }}>{formatDateTime(nearMiss.reviewedAt)}</div>
          </Card>
        )}

        {nearMiss.status === 'closed' && (
          <Card style={{ border: '0.5px solid var(--color-success)' }}>
            <div style={{ fontSize: 14, fontWeight: 400, color: 'var(--color-success)', marginBottom: 10 }}>Closed</div>
            <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginBottom: 6 }}>Corrective actions taken</div>
            <div style={{ fontSize: 13, color: 'var(--color-text-primary)', lineHeight: 1.6, marginBottom: 8 }}>{nearMiss.correctiveActions}</div>
            <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>
              Closed by {nearMiss.closedBy} · {formatDateTime(nearMiss.closedAt)}
            </div>
          </Card>
        )}

      </div>

      <Modal
        open={showCloseModal}
        onClose={() => setShowCloseModal(false)}
        title="Close near miss report"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowCloseModal(false)}>Cancel</Button>
            <Button
              variant="success"
              onClick={handleClose}
              disabled={closing || !correctiveActions.trim()}
              loading={closing}
              icon={<IconCheck size={14} stroke={1.5} />}
            >
              Close report
            </Button>
          </>
        }
      >
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>
            Corrective actions taken <span style={{ color: 'var(--color-danger)' }}>*</span>
          </label>
          <textarea
            placeholder="Describe what corrective actions were taken to prevent recurrence..."
            value={correctiveActions}
            onChange={e => setCorrectiveActions(e.target.value)}
            rows={4}
            style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }}
            autoFocus
          />
        </div>
        <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>
          This will be permanently recorded on the near miss report.
        </div>
      </Modal>

    </Layout>
  );
}